/**
 * BARREL-TILE ROOFING standards (docs/bim-plan/12-barrel-tile-spike.md, resolved
 * 2026-08-19): true two-piece pan-and-cover mission tile — pans concave-up in
 * columns running up the slope, covers flipped over the gaps between pan columns.
 * Geometric roofing is DERIVED (a function of the solved roof faces + classified
 * skeleton edges + the roof TYPE's `roofingProfile`), never a placed element —
 * the tails/fascia/gutters doctrine. These are the firm-typical dimensions Matt
 * resolved ("go with typical"); they become curated type params if the firm ever
 * wants to tune them per standard.
 *
 * Material identity is BY NAME (the firm library's adopt-by-name convention):
 * seed.ts creates these records idempotently, resolveElement materializes their
 * ids onto roof elements (ResolvedElement.roofTileMaterials) so the geometry
 * generator never needs a model handle. The Q7 color BLEND is the three clay
 * shades — tiles bucket across them deterministically by (column, course), so
 * regeneration never shimmers; the shades are ordinary material records, curated
 * in the Library panel like any other.
 */

export const ROOF_TILE_MATERIAL_NAMES = {
  blends: ['Clay Tile — Blend A', 'Clay Tile — Blend B', 'Clay Tile — Blend C'],
  mortar: 'Mortar Bed',
  flashing: 'Sheet Metal Flashing',
  /** the ASSEMBLY finish-layer material (the record of what the roofing IS —
   *  drives the auto type name and the flat mass's painted color when the
   *  Roofing system is unchecked). Kept THIN in the layer stack (~1/2″,
   *  underlayment + nominal flat build): the visible tile geometry derives ON
   *  TOP of the assembly surface, so a full tile thickness here would
   *  double-count the build-up. The blends above color the derived FIELD. */
  layer: 'Clay Barrel Tile Roofing',
} as const

export const ROOF_TILE_MATERIAL_COLORS: Record<string, string> = {
  'Clay Tile — Blend A': '#b2593a',
  'Clay Tile — Blend B': '#8f4a30',
  'Clay Tile — Blend C': '#b97e57',
  'Mortar Bed': '#b5ad9f',
  'Sheet Metal Flashing': '#9aa0a3',
  'Clay Barrel Tile Roofing': '#a8542f',
}

/** Typical two-piece clay mission tile (§7 Q1 "go with typical"). All feet. */
export const BARREL_STD = {
  tileLenFt: 18 / 12,        // tile length along the slope
  exposureFt: 14 / 12,       // course exposure (4″ headlap)
  coverSpacingFt: 9.5 / 12,  // cover columns on center
  panWFt: 8 / 12,            // pan chord width at the butt
  coverWFt: 7 / 12,          // cover chord width at the butt
  taperFt: 1 / 12,           // chord taper butt → head (what makes laps read)
  riseFt: 2.75 / 12,         // arc rise at the butt
  thickFt: 0.5 / 12,         // shell thickness (the visible butt annulus)
  capExposureFt: 14 / 12,    // ridge/hip/rake cap course exposure
  // cap-course geometry (2026-08-19 r3, Matt's "missing the ridge and hip cap
  // courses"): caps STRADDLE the joint sitting ON TOP of the field course — the
  // v1 caps were seated at the bearing surface and rendered fully buried inside
  // the field tiles (generated but invisible)
  capWFt: 10 / 12,           // cap chord width (wider than a field cover)
  capRiseFt: 3.25 / 12,      // cap arc rise
  // r4 (Matt: "caps would sit ON TOP of the main roof plane tiles — right now
  // they overlap"): the seat clears the field crowns (riseFt) entirely, so the
  // cap course rides the top of the field like a mortar-set cap, tucking just
  // slightly into the nearest crowns
  capSeatFt: 3 / 12,
  ridgeHipSetbackFt: 1.5 / 12, // field cut back each side of ridge/hip lines — the
                             // cap covers the gap; also kills the interpenetration
                             // mess where both faces' cut tiles crossed the joint
  // r5 (Matt: "roll the barge cover tiles down the face like they would be done
  // in real life"): rake/barge cap runs tilt OUTWARD about the rake axis so they
  // drape over the gable edge instead of sitting upright like ridge caps.
  // r6 tune (Matt: "the roll needs to be a bit more downward — the tile is still
  // extending out farther than it can"): 30°→45° and the outward nudge dropped
  // to zero, so the cover hugs the edge and falls down the face rather than
  // cantilevering past the barge.
  // r7/r8 tune (Matt, by eye): pulled INBOARD (negative = toward the roof) to
  // seat the rolled cover on the edge rather than pivoting on the rake line —
  // 2″ then 2″ more
  rakeRollDeg: 45,
  rakeNudgeFt: -4 / 12,
  valleyCutbackFt: 3 / 12,   // open valley: field cut back each side of center (Q4)
  valleyFlashWFt: 16 / 12,   // valley metal sheet, total width
  mortarHFt: 2.5 / 12,       // mudded eave closure height (Q2)
  mortarDFt: 3 / 12,         // mudded closure depth (in from the eave edge)
} as const

/**
 * PAINTED ROOFING (2026-09-28): the seeded roofing materials and what each builds —
 * identity BY NAME (adopt-by-name), so seeds and the one-time model upgrade agree.
 */
export const ROOFING_MATERIALS: { name: string; color: string; spec: import('./types').RoofingSpec; cut?: { pattern: string; fgColor: string; bgColor: string } }[] = [
  { name: 'Comp. Shingle Roofing', color: '#6f6a63', spec: { profile: 'flat', thicknessFt: 0.0417 }, cut: { pattern: 'shingle', fgColor: '#4a4640', bgColor: '#e4e2de' } },
  { name: ROOF_TILE_MATERIAL_NAMES.layer, color: '#b5623f', spec: { profile: 'barrel', thicknessFt: 0.0417 } },
  { name: 'Standing Seam Metal Roofing', color: '#5f666b', spec: { profile: 'standingSeam', thicknessFt: 0.0104, seamSpacingFt: 16 / 12, seamHeightFt: 1.5 / 12 } },
  // SHINGLE ROOFING (2026-10-05, Matt: "a 3D version of asphalt shingles, cedar shake, and
  // slate … asphalt shingles to feel premo, not cheap and skinny"). Real 3D courses
  // (bim-geometry roof/shingles.ts). Product facts:
  //   GAF Timberline HDZ — laminated (2 layers), 13-1/4" x 39-3/8", 5-5/8" exposure
  //   CertainTeed Presidential Shake — tri-laminate (3 layers), 14-1/4" x 40", 4" exposure
  //   Cedar shake — 24" heavy hand-split, 10" exposure, 3/4" butt, random widths, keyways
  //   Slate — 20" x 10", 3" headlap → 8-1/2" exposure, 1/4" thick, half-lap stagger
  // The butts are a touch heavier than the paper spec so the courses read at a distance.
  {
    name: 'Asphalt Shingle — GAF Timberline HDZ', color: '#4c4a47',
    spec: { profile: 'shingle', thicknessFt: 0.0417, shingle: { style: 'laminated', exposureFt: 5.625 / 12, unitFt: 39.375 / 12, buttFt: 0.3125 / 12, capExposureFt: 6.5 / 12, capWidthFt: 12 / 12 } },
    cut: { pattern: 'shingle', fgColor: '#3a3835', bgColor: '#e4e2de' },
  },
  {
    name: 'Asphalt Shingle — CertainTeed Presidential Shake', color: '#5d554d',
    spec: { profile: 'shingle', thicknessFt: 0.0417, shingle: { style: 'trilaminate', exposureFt: 4 / 12, unitFt: 40 / 12, buttFt: 0.4375 / 12, capExposureFt: 6 / 12, capWidthFt: 12 / 12 } },
    cut: { pattern: 'shingle', fgColor: '#3f3a35', bgColor: '#e4e2de' },
  },
  {
    name: 'Cedar Shake Roofing', color: '#8c6a4c',
    spec: { profile: 'shingle', thicknessFt: 0.0417, shingle: { style: 'shake', exposureFt: 10 / 12, unitFt: 8 / 12, buttFt: 0.75 / 12, gapFt: 0.5 / 12, capExposureFt: 10 / 12, capWidthFt: 12 / 12 } },
    cut: { pattern: 'shingle', fgColor: '#6b5039', bgColor: '#efe7dd' },
  },
  {
    name: 'Slate Roofing', color: '#4e555c',
    spec: { profile: 'shingle', thicknessFt: 0.0417, shingle: { style: 'slate', exposureFt: 8.5 / 12, unitFt: 10 / 12, buttFt: 0.25 / 12, gapFt: 0.125 / 12, capExposureFt: 8.5 / 12, capWidthFt: 10 / 12 } },
    cut: { pattern: 'shingle', fgColor: '#3b4045', bgColor: '#e6e8ea' },
  },
]

/**
 * CRICKET ROOFING (2026-10-19, Matt: "a new roofing finish for crickets that is basically sheet
 * metal with no standing seams that we can then use the paint brush on to change its finish"):
 * one smooth sheet (26 ga, the 'flat' profile builds a plain sheet of this thickness over the
 * plane — no seams, so no pattern lines), zinc by default, SHEET METAL substrate so the Paint
 * Brush can make it copper, blackened or hot-rolled steel, or a factory / coating color. Its
 * firm-library record is `roofmatSHM01`; every new cricket is painted with it.
 */
export const CRICKET_ROOFING = {
  id: 'roofmatSHM01', name: 'Flat Sheet Metal Roofing', category: 'Roofing',
  color: '#8a8f93', surface: { color: '#8a8f93' }, cut: { pattern: 'solid', fgColor: '#5a5e61', bgColor: '#dfe1e2' },
  roofing: { profile: 'flat', thicknessFt: 0.0104 } as import('./types').RoofingSpec,
  render: { metalness: 1, roughness: 0.55 }, substrate: 'sheetMetal',
} as const

/** SHINGLE ROOFING: the shades the shingle blends USED to be laid in. MATERIAL CLEANUP slice 2
 *  (2026-10-06): no longer seeded — pieces vary in shades of the finish's one color; these names
 *  are kept so the one-time cleanup can find and remove the old records */
export const ROOFING_BLEND_MATERIALS: { name: string; color: string }[] = [
  { name: 'Asphalt Shingle — Shadow', color: '#2b2a28' },
  { name: 'Asphalt Shingle — Charcoal Accent', color: '#5c5955' },
  { name: 'Asphalt Shingle — Presidential Shadow', color: '#352f2a' },
  { name: 'Asphalt Shingle — Presidential Accent', color: '#6f665c' },
  { name: 'Cedar Shake — Blend A', color: '#8f6b4b' },
  { name: 'Cedar Shake — Blend B', color: '#a3805c' },
  { name: 'Cedar Shake — Blend C', color: '#76573d' },
  { name: 'Slate — Blend A', color: '#4b525a' },
  { name: 'Slate — Blend B', color: '#5a5f66' },
  { name: 'Slate — Blend C', color: '#41444b' },
]
/** the spec a painted material builds (a material without one paints a flat 1/2" sheet) */
export function roofingSpecOf(m: { roofing?: import('./types').RoofingSpec } | null | undefined): import('./types').RoofingSpec {
  const r = m?.roofing
  if (r && (r.profile === 'flat' || r.profile === 'barrel' || r.profile === 'standingSeam' || (r.profile === 'shingle' && !!r.shingle))) return r
  return { profile: 'flat', thicknessFt: 0.0417 }
}
